import { apiRequest } from "./client";

export type Status = "Pending" | "Acknowledged";
export type FilterStatus = "Pending" | "Acknowledged" | "Attachments";

export type GrievanceListItem = {
  grievance_id: number;
  application_id: string;
  grievance_preview: string;
  status: Status;
  sent_date_time: string | null;
  responded_by_username: string | null;
  responded_by_full_name: string | null;
  responded_action_type: string | null;
  responded_at: string | null;
};

export type GrievanceAction = {
  id: number;
  admin_id: number;
  admin_username: string | null;
  admin_full_name: string | null;
  action_type: "generated" | "submitted" | "edited_submitted";
  ai_text: string | null;
  edited_text: string | null;
  acted_at: string;
};

export type GrievanceListResponse = {
  items: GrievanceListItem[];
  next_cursor: number | null;
  has_more: boolean;
  total_estimate: number;
};

export type GrievanceDetail = {
  grievance_id: number;
  application_id: string;
  grievance: string;
  sent_date_time: string | null;
  status: Status;
  bot_response: string | null;
  final_reply: string | null;
  replied_by: string | null;
  replied_date_time: string | null;
  candidate_name: string | null;
  has_attachment: boolean;
  actions: GrievanceAction[];
  current_status_id: number | null;
};

export type HistoryItem = {
  grievance_id: number;
  grievance: string;
  sent_date_time: string | null;
  replied_grievance: string | null;
  status: Status;
  is_test: boolean;
};

export type SortDir = "asc" | "desc";

export type ListParams = {
  status: FilterStatus[];
  q: string;
  limit?: number;
  cursor?: number | null;
  offset?: number | null;
  sort?: SortDir;
};

function base(tabId: string) {
  return `/admin/tabs/${tabId}`;
}

export function listGrievances(tabId: string, params: ListParams) {
  return apiRequest<GrievanceListResponse>(`${base(tabId)}/tickets`, {
    query: {
      status: params.status,
      q: params.q || undefined,
      limit: params.limit ?? 50,
      cursor: params.cursor ?? undefined,
      offset: params.offset ?? undefined,
      // Only forward when non-default so the URL stays clean when sorted DESC.
      sort: params.sort === "asc" ? "asc" : undefined,
    },
  });
}

export function getGrievance(tabId: string, id: number) {
  return apiRequest<GrievanceDetail>(`${base(tabId)}/tickets/${id}`);
}

export type AdjacentIds = {
  prev_id: number | null;
  next_id: number | null;
};

export function getAdjacent(
  tabId: string,
  id: number,
  filters: { status: FilterStatus[]; q: string; sort?: SortDir },
) {
  return apiRequest<AdjacentIds>(`${base(tabId)}/tickets/${id}/adjacent`, {
    query: {
      status: filters.status,
      q: filters.q || undefined,
      sort: filters.sort === "asc" ? "asc" : undefined,
    },
  });
}

export function getHistory(
  tabId: string,
  id: number,
  applicationId?: string,
  includeTest = false,
) {
  const params = new URLSearchParams();
  if (applicationId) params.set("application_id", applicationId);
  if (includeTest) params.set("include_test", "true");
  const qs = params.toString();
  return apiRequest<{ items: HistoryItem[] }>(
    `${base(tabId)}/tickets/${id}/history${qs ? `?${qs}` : ""}`,
  );
}

export type ApplicationStatus = {
  verification_mode: "E" | "P" | "";
  status_text: string;
  status_color: "green" | "orange" | null;
  pending_action:
    | "lock_and_submit"
    | "resubmit"
    | "edit_and_resubmit"
    | "book_slot"
    | null;
};

export function getApplicationStatus(tabId: string, id: number) {
  return apiRequest<ApplicationStatus>(
    `${base(tabId)}/tickets/${id}/application-status`,
  );
}

export function generateResponse(tabId: string, id: number) {
  return apiRequest<{ bot_response: string }>(`${base(tabId)}/tickets/${id}/generate`, {
    method: "POST",
  });
}

export function sendReply(tabId: string, id: number, responseText: string) {
  return apiRequest<{ ok: boolean; replied_by: string }>(`${base(tabId)}/tickets/${id}/reply`, {
    method: "POST",
    body: { response_text: responseText },
  });
}

export function markAsViewed(tabId: string, id: number) {
  return apiRequest<{ ok: boolean; current_status_id: number }>(
    `${base(tabId)}/tickets/${id}/mark-viewed`,
    { method: "POST" },
  );
}
